import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, BookOpen, Users, Award, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const captainIcon = new L.DivIcon({
  className: 'captain-marker',
  html: `<div style="width:48px;height:48px;background:linear-gradient(135deg,#d97706,#f59e0b);border:3px solid #fcd34d;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 0 20px rgba(217,119,6,0.6),0 0 40px rgba(217,119,6,0.3);animation:pulse 2s ease-in-out infinite;position:relative"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/></svg></div><style>@keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.1)}}</style>`,
  iconSize: [48, 48],
  iconAnchor: [24, 24],
  popupAnchor: [0, -28],
});

const guardIcon = new L.DivIcon({
  className: 'guard-marker',
  html: `<div style="width:20px;height:20px;background:linear-gradient(135deg,#d97706,#f59e0b);border:2px solid #fcd34d;border-radius:50%;box-shadow:0 2px 8px rgba(217,119,6,0.4)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -14],
});

const KingdomGuard = () => {
  const navigate = useNavigate();
  const [guards, setGuards] = useState([]);
  const [stats, setStats] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    location: '',
    eventType: '',
    dates: '',
    trainingInterest: '',
    referral: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/escort-guard/public/guards`)
      .then(r => r.json())
      .then(d => { setGuards(d.guards || []); setStats(d.stats || {}); })
      .catch(console.error);
  }, []);

  const handleSubscribe = (rank, price) => {
    toast.info(`Kingdom Escort Guard memberships coming soon! Subscribe to ${rank} for $${price}/month.`);
    navigate('/subscriptions');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // For now, just show success message
      // TODO: Create backend endpoint for Kingdom Escort Guard inquiries
      toast.success('Thank you! Mordecai will contact you personally within 2-3 days.');
      setFormData({
        name: '',
        organization: '',
        location: '',
        eventType: '',
        dates: '',
        trainingInterest: '',
        referral: ''
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const guardTiers = [
    {
      rank: 'Watchman',
      price: 9,
      yearlyPrice: 79,
      subscription: 'Berean',
      discount: '10%',
      features: [
        'Berean subscription included',
        'Level 1: The First Breath training',
        'Kingdom Escort Guard registry listing',
        'Shofar theology foundations',
        'Four biblical calls instruction',
        '10% off all shofar products'
      ]
    },
    {
      rank: 'Guard',
      price: 15,
      yearlyPrice: 129,
      subscription: 'Scholar',
      discount: '15%',
      popular: true,
      features: [
        'Scholar subscription included ($19 value)',
        'Levels 1 & 2 training',
        'Mastery of all four calls',
        'Certification test with grading',
        'Priority church placement',
        '15% off all products'
      ]
    },
    {
      rank: 'Captain',
      price: 22,
      yearlyPrice: 189,
      subscription: 'Scholar + Ambassador',
      discount: '20%',
      features: [
        'Scholar + Ambassador access',
        'Levels 1, 2 & 3 training',
        'Train others certification',
        'Corporate assembly leadership',
        '25% referral commission',
        '20% product discount'
      ]
    },
    {
      rank: 'Commander',
      price: 35,
      yearlyPrice: 299,
      subscription: 'Pastor/Church',
      discount: '25%',
      features: [
        'Pastor/Church tier included ($49 value)',
        'All 4 levels + direct mentorship',
        'Advanced prophetic training',
        'Regional chapter leadership',
        '30% referral commission',
        'Roll of Honor listing'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1658343546442-85e82a8d5186?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxyYW0lMjBob3JufGVufDB8fHx8MTc3MzUzNTgxNHww&ixlib=rb-4.1.0&q=85"
            alt="Shofar"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-amber-600 text-white text-sm px-4 py-1">
              <Shield className="inline h-4 w-4 mr-2" />
              The Kingdom Escort Guard
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold">
              Trained Shofar Blowers<br />Across the Nation
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              A private registry of certified shofar blowers available to churches, ministries, and events. Professional training. Biblical foundation. Kingdom deployment.
            </p>
          </div>
        </div>
      </section>

      {/* Global Guard Map */}
      <section className="py-16 bg-slate-900" data-testid="public-guard-map-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-[10px] tracking-[0.4em] uppercase font-mono text-amber-600/60 mb-2">Global Presence</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Our Guards Across the World</h2>
            <p className="text-amber-500/60 text-sm max-w-xl mx-auto">
              {stats.total || 0} trained guards across {stats.countries || 0} countries, standing watch and sounding the call
            </p>
          </div>
          <div className="max-w-5xl mx-auto rounded-xl overflow-hidden border border-amber-900/30" style={{ height: '450px' }}>
            <MapContainer
              center={[25, -10]}
              zoom={2}
              style={{ height: '100%', width: '100%', background: '#1a1510' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; CartoDB'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              {guards.map(g => {
                const isCaptain = g.title === 'Captain of the Guard';
                return (
                  <Marker
                    key={g.guard_id}
                    position={[g.lat, g.lng]}
                    icon={isCaptain ? captainIcon : guardIcon}
                    zIndexOffset={isCaptain ? 1000 : 0}
                  >
                    <Popup>
                      <div style={{ minWidth: isCaptain ? 200 : 140, fontFamily: 'serif' }}>
                        {isCaptain ? (
                          <div style={{ background: 'linear-gradient(135deg, #78350f, #92400e)', color: 'white', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '9px', letterSpacing: '3px', textTransform: 'uppercase', color: '#fcd34d', marginBottom: '4px' }}>Captain of the Guard</div>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#fef3c7' }}>{g.name}</div>
                            <div style={{ fontSize: '12px', color: '#d4a' + '574', marginTop: '4px' }}>{g.city}, {g.country}</div>
                            <div style={{ fontSize: '11px', color: '#fcd34d', marginTop: '6px' }}>{g.instrument}</div>
                            {g.phone && <div style={{ fontSize: '11px', color: '#fcd34d', marginTop: '2px' }}>{g.phone}</div>}
                          </div>
                        ) : (
                          <div style={{ padding: '4px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{g.name}</div>
                            <div style={{ fontSize: '11px', color: '#666' }}>{g.city}, {g.country}</div>
                            <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>{g.instrument}</div>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>
      </section>

      {/* What is the Kingdom Escort Guard */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
              What is the Kingdom Escort Guard?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-xl leading-relaxed">
                The Kingdom Escort Guard is a private ministry registry of trained shofar blowers who are available to serve churches, ministries, and events across the country. This is NOT a public marketplace - it's a personal ministry connection facilitated by Mordecai himself.
              </p>
              <p className="text-lg leading-relaxed">
                When a church or ministry submits an inquiry, Mordecai personally reviews Kingdom Escort Guard members in that region and makes direct introductions. Every placement is handled with care, ensuring theological alignment and ministry compatibility.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <BookOpen className="h-8 w-8 text-amber-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Biblical Training</h3>
                <p className="text-gray-600">Four-level certification program teaching theology, technique, and deployment</p>
              </div>
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Users className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Ministry Connection</h3>
                <p className="text-gray-600">Personal placement by Mordecai ensures theological and ministry fit</p>
              </div>
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Award className="h-8 w-8 text-green-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Certification Ranks</h3>
                <p className="text-gray-600">Progress from Watchman to Commander with increasing responsibility</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guard Membership Tiers */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Kingdom Escort Guard Membership Tiers
            </h2>
            <p className="text-xl text-gray-600">
              Each rank includes professional shofar training + full digital subscription access
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {guardTiers.map((tier) => (
              <Card key={tier.rank} className={`flex flex-col ${tier.popular ? 'border-amber-500 border-2 shadow-xl scale-105' : ''}`}>
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-amber-600 text-white">
                    Most Popular
                  </Badge>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl font-bold">{tier.rank}</CardTitle>
                  <div className="mt-4">
                    <div className="text-4xl font-bold text-gray-900">${tier.price}</div>
                    <div className="text-gray-600 text-sm mt-1">per month</div>
                    <div className="text-sm text-gray-500 mt-2">or ${tier.yearlyPrice}/year</div>
                  </div>
                  <div className="mt-4 space-y-1">
                    <Badge variant="outline" className="text-xs">{tier.subscription}</Badge>
                    <p className="text-sm text-gray-600">{tier.discount} store discount</p>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow">
                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-green-600 flex-shrink-0">✓</span>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    className={`w-full ${tier.popular ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                    onClick={() => handleSubscribe(tier.rank, tier.price)}
                  >
                    Join as {tier.rank}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12 text-gray-600">
            <p className="text-sm">All Guard memberships include digital subscription + shofar training + store discounts</p>
          </div>
        </div>
      </section>

      {/* Request a Shofar Blower Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                Request a Kingdom Escort Guard Blower
              </h2>
              <p className="text-xl text-gray-600">
                Fill out this inquiry and Mordecai will personally connect you with trained blowers in your area
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Kingdom Escort Guard Inquiry Form</CardTitle>
                <CardDescription>
                  All inquiries are handled personally by Mordecai. You'll receive a response within 2-3 business days.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Pastor John Smith"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organization">Church or Organization Name *</Label>
                    <Input
                      id="organization"
                      required
                      value={formData.organization}
                      onChange={(e) => setFormData({...formData, organization: e.target.value})}
                      placeholder="Grace Community Church"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location">Location (City and State) *</Label>
                    <Input
                      id="location"
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="Nashville, TN"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="eventType">Type of Event *</Label>
                    <Select
                      value={formData.eventType}
                      onValueChange={(value) => setFormData({...formData, eventType: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single Event</SelectItem>
                        <SelectItem value="weekly">Weekly Worship Service</SelectItem>
                        <SelectItem value="feast">Feast Day Service</SelectItem>
                        <SelectItem value="revival">Revival / Conference</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dates">Date(s) Needed</Label>
                    <Input
                      id="dates"
                      value={formData.dates}
                      onChange={(e) => setFormData({...formData, dates: e.target.value})}
                      placeholder="September 15-17, 2026"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="trainingInterest">Training Interest</Label>
                    <Select
                      value={formData.trainingInterest}
                      onValueChange={(value) => setFormData({...formData, trainingInterest: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your interest" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="event-only">Event coverage only</SelectItem>
                        <SelectItem value="train-member">Train a congregation member permanently</SelectItem>
                        <SelectItem value="both">Both - event coverage + training</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="referral">How did you hear about the Kingdom Escort Guard?</Label>
                    <Textarea
                      id="referral"
                      value={formData.referral}
                      onChange={(e) => setFormData({...formData, referral: e.target.value})}
                      placeholder="Podcast, Ambassador referral, YouTube, etc."
                      rows={3}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white" 
                    size="lg"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Inquiry'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700 text-center">
                <strong>What happens next?</strong> Your inquiry goes directly to Mordecai. He'll review Kingdom Escort Guard members in your region and contact you personally within 2-3 days with available options and next steps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shofar Training Levels */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Four Levels of Training
            </h2>
            <p className="text-xl text-gray-600">
              From first breath to regional leadership
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                level: 'Level 1: The First Breath',
                rank: 'Watchman',
                content: 'Theology of the shofar · Four biblical calls introduction · First tone technique · Instrument care',
                duration: '4 video lessons + PDF guide'
              },
              {
                level: 'Level 2: The Four Calls',
                rank: 'Guard',
                content: 'Master Tekiah, Shevarim, Teruah, Tekiah Gedolah · Feast calendar · Lead congregation · Certification test',
                duration: '6 video lessons + graded submission'
              },
              {
                level: 'Level 3: The Deployment',
                rank: 'Captain',
                content: 'Corporate assembly leadership · Multiple blower coordination · Train others · Live virtual practicum',
                duration: '8 video lessons + certification'
              },
              {
                level: 'Level 4: Commander\'s School',
                rank: 'Commander (Invitation Only)',
                content: 'Advanced prophetic use · Jewish traditions · Regional chapter building · Spiritual warfare · Direct mentorship',
                duration: 'Mentorship program with Mordecai'
              }
            ].map((training, idx) => (
              <Card key={idx} className="border-2">
                <CardHeader>
                  <Badge className="w-fit mb-2">{training.rank}</Badge>
                  <CardTitle className="text-xl">{training.level}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">{training.content}</p>
                  <p className="text-sm text-gray-500 font-medium">{training.duration}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to Sound the Trumpet?
            </h2>
            <p className="text-xl text-amber-100">
              Join the Kingdom Escort Guard and bring the shofar's voice back to the body of Messiah
            </p>
            <Button 
              size="lg" 
              className="bg-white text-amber-700 hover:bg-gray-100"
              onClick={() => navigate('/subscriptions')}
            >
              Register as Kingdom Escort Guard
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KingdomGuard;
